import React, { lazy, useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import Layout from 'atomic-layout';

import 'intersection-observer';
import '@/styles/scss/bulma-compat.scss';
import './app.scss';
import '@/ui/utils/bootstrapIcons';
import { LoginPage } from '@/pages/auth/LoginPage';
import { CatalogPage } from '@/pages/Catalog/CatalogPage';
import { CourseDetailsPage } from '@/pages/learning/CourseDetails/CourseDetailsPage';
import { LearningCollectionPage } from '@/pages/learning/LearningCollection/LearningCollectionPage';
import { NotificationsPage } from '@/pages/user/Notifications/NotificationsPage';
import { PostPage } from '@/pages/PostPage';
import { ReflectionInjection } from '@/pages/ReflectionInjection';
import { OnboardingPage } from '@/pages/Onboarding/OnboardingPage';
import { TermsPage } from '@/pages/Terms/TermsPage';
import { PrivacyPage } from '@/pages/Terms/PrivacyPage';
import { AboutUsPage } from '@/pages/AboutUsPage';
import { ELearningView } from '@/pages/learning/ELearningView';
import { ArticleRedirect } from '@/component/ArticleRedirect/ArticleRedirect';
import { AuthenticatedRoute } from './component/AuthenticatedRoute/AuthenticatedRoute';
import { HistoricCompletedCourses } from '@/pages/user/MyLearning/components/HistoricCompletedCourses';
import { Modal } from '@/components/Modal/Modal';
import { SuperAdminRoute } from './component/SuperAdminRoute/SuperAdminRoute';
import { useCurrentUser } from '@/store/currentUser';
import { ConfirmationModal } from '@/components/Modal/ConfirmationModal';
import { usePageview } from '@/utils/tracking/countly';
import { ProviderPage } from '@/pages/Provider/ProviderPage';
import { SearchPage } from '@/pages/Search/SearchPage';
import { CategoryPage } from '@/pages/Catalog/CategoryPage';
import { SubjectPage } from '@/pages/Catalog/SubjectPage';
import { AllProvidersPage } from '@/pages/AllProviders/AllProvidersPage';
import { checkAdminView } from '@/utils/checkAdminView';
import { EventsPage } from '@/pages/Catalog/EventsPage';
import { useIsSimpleModeEnabled } from '@/feature-toggles';
import { Homepage } from '@/pages/Homepage';
import { LegacyGlobalStyle } from '@/styles';
import { BuddyThemeProvider } from '@/ui/theme';
import { LayoutWrapper } from './component/LayoutWrapper/LayoutWrapper';
import { ManagerNavigate } from './component/ManagerNavigate';
import { Assignment } from '@/pages/Assignment';
import { ParticipantsPage } from '@/pages/Assignment/ParticipantsPage';
import { ManagerRoute } from '@/component/App/component/ManagerRoute';
import { ScrollToTop } from '@/component/ScrollToTop';
import { BundleChecker } from '@/component/BundleChecker/BundleChecker';
import { ErrorBoundary } from '@/component/ErrorBoundary';
import { JourneyView } from '@/features/journey/admin/pages/JourneyView';
import { JourneyEditViewParticipants } from '@/features/journey/admin/organisms/JourneyEditViewParticipants';
import { LearningRouter } from '@/features/course/learner/pages/LearningRouter';
import { JourneyEditViewDiscussions } from '@/features/journey/admin/pages/JourneyEditViewDiscussions';
import { JourneyEditViewTeams } from '@/features/journey/admin/templates/JourneyEditViewTeams';
import { JourneyTeamsCsvImportPage } from '@/features/csvImport/admin/pages/JourneyTeamsCsvImportPage';
import { JourneyCertificatePage } from '@/pages/user/JourneyCertificate';
import { ROUTE_PATH, ROUTE_RELATIVE_PATH } from '@/constants/routePaths';

const MyLearningPage = lazy(() => import('@/pages/user/MyLearning/MyLearningPage'));
const Page404 = lazy(() => import('@/pages/Page404'));
const Styleguide = lazy(() => import('@/pages/Styleguide/Styleguide'));
const ProfilePage = lazy(() => import('@/pages/user/Profile/ProfilePage'));
const Administration = lazy(() => import('@/administration/Administration'));
const Management = lazy(() => import('@/features/management/manager/molecules/ManagementWithProviders/ManagementWithProviders'));
const Checklist = lazy(() => import('@/pages/SuperAdmin/Checklist/Checklist'));

const App = (): JSX.Element => {
  const { loadingFirstTime, error } = useCurrentUser();
  const [isSimpleMode] = useIsSimpleModeEnabled();
  const isAdminView = checkAdminView();

  usePageview();

  useEffect(() => {
    Layout.configure({ defaultUnit: 'rem' });
  }, []);

  useEffect(() => {
    if (isAdminView && !document.title.match(/.+- Admin/g)) {
      document.title = `${document.title} - Admin`;
    }
  }, [isAdminView]);

  useEffect(() => {
    if (!isAdminView && document.title.match(/.+- Admin/g)) {
      document.title = `${document.title.replace(/\s- Admin/g, '')}`;
    }
  }, [isAdminView]);

  // Only show loader if user info is being loaded *for the first time*; otherwise
  // if the user is refetched (eg. after changing username), the current component tree
  // is unmounted, the loader is rendered instead for a brief moment,
  // and then the component tree is rendered again from scratch - losing its previous state.
  if (loadingFirstTime && !error) return <div className="pageloader pageloader--full" />;

  return (
    <BuddyThemeProvider>
      <ErrorBoundary>
        <LegacyGlobalStyle />

        <Modal />
        <ConfirmationModal />

        <ScrollToTop />
        <BundleChecker />

        <Routes>
          <Route path={ROUTE_RELATIVE_PATH.INJECT_REFLECTION} element={<ReflectionInjection />} />

          <Route element={<LayoutWrapper />}>
            <Route path={ROUTE_RELATIVE_PATH.NOT_FOUND} element={<Page404 />} />
            <Route path={ROUTE_RELATIVE_PATH.LOGIN} element={<LoginPage />} />
            <Route path={ROUTE_RELATIVE_PATH.TERMS} element={<TermsPage />} />
            <Route path={ROUTE_RELATIVE_PATH.PRIVACY} element={<PrivacyPage />} />
            <Route path={ROUTE_RELATIVE_PATH.ABOUT} element={<AboutUsPage />} />
          </Route>

          <Route element={<AuthenticatedRoute />}>
            <Route path="/administration/*" element={<Administration />} />

            <Route element={<LayoutWrapper />}>
              <Route path={ROUTE_RELATIVE_PATH.MANAGEMENT} element={<ManagerRoute />}>
                <Route index element={<Navigate to="analyze/learners" replace />} />
                <Route path={ROUTE_RELATIVE_PATH.WILDCARD} element={<Management />} />
              </Route>
              <Route path={ROUTE_PATH.MANAGER_USER} element={<ManagerNavigate />} />
              <Route path={ROUTE_RELATIVE_PATH.MANAGER_ANALYTICS} element={<ManagerNavigate />} />
            </Route>

            {/**
             * No route rendering ViewLearning should be
             * wrapped in LayoutWrapper, since it's rendering it itself conditionally
             * */}
            <Route path={ROUTE_RELATIVE_PATH.LEARNING_ROOT}>
              <Route path={ROUTE_RELATIVE_PATH.LEARNING} element={<LearningRouter />} />
              <Route path={ROUTE_RELATIVE_PATH.LEARNING_NAMED} element={<LearningRouter />} />
            </Route>
            <Route path={ROUTE_RELATIVE_PATH.COLLECTION_ROOT}>
              <Route path={ROUTE_RELATIVE_PATH.COLLECTION_LEARNING} element={<LearningRouter />} />
              <Route path={ROUTE_RELATIVE_PATH.COLLECTION_LEARNING_NAMED} element={<LearningRouter />} />
            </Route>
            <Route path={ROUTE_RELATIVE_PATH.JOURNEY_CERTIFICATE} element={<JourneyCertificatePage />} />
            {/* The import wizard owns the whole viewport, so it renders outside LayoutWrapper */}
            <Route path={ROUTE_RELATIVE_PATH.JOURNEY_DISCUSSIONS_TEAMS_IMPORT} element={<JourneyTeamsCsvImportPage />} />
            {/** Learner routes, that are always wrapped with LayoutWrapper*/}
            <Route element={<LayoutWrapper />}>
              <Route path={ROUTE_RELATIVE_PATH.HOME} element={isSimpleMode ? <CatalogPage /> : <Homepage />} />
              <Route path={ROUTE_RELATIVE_PATH.HOME_SLASH} element={isSimpleMode ? <CatalogPage /> : <Homepage />} />
              <Route path={ROUTE_RELATIVE_PATH.ONBOARDING} element={<OnboardingPage />} />
              <Route path={ROUTE_RELATIVE_PATH.NOTIFICATIONS} element={<NotificationsPage />} />
              <Route path={ROUTE_RELATIVE_PATH.ASSIGNMENT_ROOT}>
                <Route index element={<Assignment />} />
                <Route path={ROUTE_RELATIVE_PATH.ASSIGNMENT_PARTICIPANTS} element={<ParticipantsPage />} />
              </Route>

              <Route path={ROUTE_RELATIVE_PATH.JOURNEY_ROOT}>
                <Route index element={<JourneyView />} />
                <Route path={ROUTE_RELATIVE_PATH.JOURNEY_SECTION} element={<JourneyView />} />
                <Route path={ROUTE_RELATIVE_PATH.JOURNEY_SESSION_PARTICIPANTS} element={<JourneyEditViewParticipants />} />
                <Route path={ROUTE_RELATIVE_PATH.JOURNEY_DISCUSSIONS} element={<JourneyEditViewDiscussions />} />
                <Route path={ROUTE_RELATIVE_PATH.JOURNEY_DISCUSSIONS_TEAMS_SETUP} element={<JourneyEditViewTeams />} />
              </Route>

              <Route path={ROUTE_RELATIVE_PATH.LEARNING_ROOT}>
                <Route index element={<CatalogPage />} />
                <Route path={ROUTE_RELATIVE_PATH.CATALOG_EVENTS} element={<EventsPage />} />
                <Route path={ROUTE_RELATIVE_PATH.CATALOG_SEARCH} element={<SearchPage />} />
                <Route path={ROUTE_RELATIVE_PATH.COURSE_DETAILS} element={<CourseDetailsPage />} />
                <Route path={ROUTE_RELATIVE_PATH.COURSE} element={<CourseDetailsPage />} />
                <Route path={ROUTE_RELATIVE_PATH.CATALOG_PROVIDERS} element={<AllProvidersPage />} />
                <Route path={ROUTE_RELATIVE_PATH.CATALOG_PROVIDER} element={<ProviderPage />} />
                <Route path={ROUTE_RELATIVE_PATH.CATALOG_CATEGORY} element={<CategoryPage />} />
                <Route path={ROUTE_RELATIVE_PATH.CATALOG_SUBJECT} element={<SubjectPage />} />
              </Route>
              <Route path={ROUTE_RELATIVE_PATH.COLLECTION_ROOT}>
                <Route index element={<LearningCollectionPage />} />
                <Route path={ROUTE_RELATIVE_PATH.WILDCARD} element={<LearningCollectionPage />} />
                <Route path={ROUTE_RELATIVE_PATH.COLLECTION_COURSE_DETAILS} element={<CourseDetailsPage />} />
                <Route path={ROUTE_RELATIVE_PATH.COLLECTION_COURSE} element={<CourseDetailsPage />} />
                <Route path={ROUTE_RELATIVE_PATH.COLLECTION_ARTICLE} element={<ArticleRedirect />} />
              </Route>
              <Route path={ROUTE_PATH.POST} element={<PostPage />} />
              <Route path={ROUTE_PATH.REFLECTION} element={<PostPage />} />

              <Route path={ROUTE_PATH.PROFILE} element={<ProfilePage />} />

              <Route path={ROUTE_PATH.MY_LEARNING_HR_ONLINE_DATA} element={<HistoricCompletedCourses />} />
              <Route path={ROUTE_PATH.MY_LEARNING_ALL} element={<MyLearningPage />} />

              <Route
                path={ROUTE_RELATIVE_PATH.STYLEGUIDE}
                element={<Navigate to={ROUTE_PATH.SUPER_ADMIN_STYLEGUIDE} replace />}
              />
              <Route
                path={ROUTE_RELATIVE_PATH.STYLEGUIDE_SECTION}
                element={<Navigate to={ROUTE_PATH.SUPER_ADMIN_STYLEGUIDE_SECTION} replace />}
              />
              <Route path={ROUTE_PATH.SUPER_ADMIN} element={<SuperAdminRoute />}>
                <Route path={ROUTE_RELATIVE_PATH.STYLEGUIDE} element={<Styleguide />}>
                  <Route path={ROUTE_RELATIVE_PATH.SUPER_ADMIN_STYLEGUIDE_SECTION} />
                </Route>
                <Route path={ROUTE_RELATIVE_PATH.SUPER_ADMIN_CHECKLIST} element={<Checklist />} />
              </Route>

              <Route path={ROUTE_RELATIVE_PATH.WILDCARD} element={<Page404 />} />
            </Route>

            <Route path={ROUTE_RELATIVE_PATH.LEARNING_ROOT}>
              <Route path={ROUTE_RELATIVE_PATH.E_LEARNING_VIEW} element={<ELearningView />} />
              <Route path={ROUTE_RELATIVE_PATH.E_LEARNING_VIEW_NAMED} element={<ELearningView />} />
            </Route>
          </Route>
        </Routes>
      </ErrorBoundary>
    </BuddyThemeProvider>
  );
};

export { App };
